/* eslint-disable react/display-name */
import * as React from 'react'
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core'
import { formatDate } from '../../../utils/dates'
import { UserFacingError } from '../../../errors/UserFacingError'
import { NetworkError } from '../../../errors/NetworkError'
import { logError } from '../../../logging'
import { IHazardComment } from '../../../models/Hazard'
import { HSCommentDialog } from './HSCommentDialog'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../../OneplaceLibraryProvider'

export interface IHSCommentsState {
  comments?: IHazardComment[]
  addCommentDialogOpen: boolean
  submitDialogOpen: boolean
  submitDialogMessage?: string
  submitDialogSubMessage?: string
}

export interface IHSCommentsProp extends IOneplaceLibraryContextProp {
  dateTimeFormat: string
  hazardId: number
  comments?: IHazardComment[]
  onCommentAdded: (comment: string) => Promise<IHazardComment>
}

export default withOneplaceLibraryContext(
  class extends React.Component<IHSCommentsProp, IHSCommentsState> {
    constructor(props: IHSCommentsProp) {
      super(props)
      this.state = {
        comments: props.comments,
        addCommentDialogOpen: false,
        submitDialogOpen: false
      }
    }

    submitDialogOnClose = () => {
      this.setState({ submitDialogOpen: false })
    }

    onCommentAdded = async (comment: string) => {
      const t = this.props.ctx.i18next.t
      this.setState({
        addCommentDialogOpen: false,
        submitDialogOpen: true,
        submitDialogMessage: '',
        submitDialogSubMessage: ''
      })

      try {
        const newComment = await this.props.onCommentAdded(comment)

        this.setState((prevState) => ({
          ...prevState,
          comments: [newComment, ...(prevState.comments || [])],
          submitDialogTitle: 'Submit Successful',
          submitDialogMessage: t('comment_submit_successful')
        }))
      } catch (e) {
        if (e instanceof UserFacingError) {
          this.setState({
            submitDialogMessage: t('comment_submit_problem'),
            submitDialogSubMessage: e.message
          })
        } else if (e instanceof NetworkError) {
          this.setState({
            submitDialogMessage: t('comment_network_error'),
            submitDialogSubMessage: undefined
          })
        } else {
          logError(e, 'Unexpected error while submitting comment')
          this.setState({
            submitDialogMessage: t('comment_submit_error'),
            submitDialogSubMessage: undefined
          })
        }
      }
    }
    render() {
      const t = this.props.ctx.i18next.t
      return (
        <Card style={{ marginTop: 12 }}>
          <CardContent>
            <div style={{ display: 'flex' }}>
              <Typography style={{ flexGrow: 1 }} variant='h5'>
                {t('comments')}
              </Typography>

              <Button
                variant='contained'
                color='secondary'
                onClick={() => this.setState({ addCommentDialogOpen: true })}
              >
                {t('add_comment')}
              </Button>
            </div>
            {this.state.comments &&
              this.state.comments.map((comment, idx) => {
                const date = formatDate(
                  'display_datetime',
                  comment.date,
                  this.props.dateTimeFormat
                )
                return (
                  <CardHeader
                    key={idx}
                    avatar={
                      <Avatar aria-label='Recipe'>
                        {comment.name.substr(0, 1).toUpperCase()}
                      </Avatar>
                    }
                    title={date + ' - ' + comment.name}
                    subheader={
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {comment.comment}
                      </span>
                    }
                  />
                )
              })}
            {this.state.addCommentDialogOpen && (
              <HSCommentDialog
                isOpen={this.state.addCommentDialogOpen}
                onClose={() => this.setState({ addCommentDialogOpen: false })}
                onComment={this.onCommentAdded}
                comment=''
              />
            )}
            <Dialog
              open={this.state.submitDialogOpen}
              onClose={this.submitDialogOnClose}
            >
              <DialogTitle>{t('add_comment')}</DialogTitle>
              <DialogContent>{this.state.submitDialogMessage}</DialogContent>
              <DialogActions>
                <Button color='primary' onClick={this.submitDialogOnClose}>
                  {t('close')}
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      )
    }
  }
)
